import './virtual-high-school.scss';
import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import * as media from './virtual-high-school-media';
import CommonButton from '../../../../components/button/button';
import StopMotionSvg from '../../../../components/StopMotionSvg';
import { Trans, useTranslation } from 'react-i18next';

const VirtualHighSchool = () => {
    const { t } = useTranslation();
    const pageRef = useRef();
    const [interval, setInterval] = useState(null);

    // Same idea as the videos page section 2: lines fade in on scroll, then start dancing
    useGSAP(() => {
        gsap.registerPlugin(ScrollTrigger);
        const tl = gsap.timeline();
        ScrollTrigger.create({
            animation: tl,
            trigger: '.section-6 .steps',
            start: 'top 80%',
            end: 'top 35%',
            scrub: true,
        });
        tl.fromTo('.section-6 .step .lines',
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: 1, stagger: 0.35 })
            .add(() => setInterval(i => !i ? 400 : null), '-=0.2');
    }, { scope: pageRef });

    return (
        <div className="containerPage" ref={pageRef}>
            <div
                onClick={(e) => e.stopPropagation()} // Prevent click from closing modal
                className="virtual-high-school-component">
                <section className='section-1'>
                    <h1 className='gefen-program-header'>
                        <span className='title-small'>{t('gefen.virtual-high-school.title1')}</span>
                        <span className='title-big'>{t('gefen.virtual-high-school.title2')}</span>
                    </h1>
                </section>

                <section className='section-2'>
                    <div className='logo'><img src={media.logo} alt='' /></div>
                    <p className='tagline'>
                        <Trans i18nKey='gefen.virtual-high-school.section1.tagline'>
                            <span className='marked-text'></span>
                        </Trans>
                    </p>
                    <p className='sub-tagline'>{t('gefen.virtual-high-school.section1.subTagline')}</p>
                </section>

                <section className='section-3'>
                    <div className='flex-column'>
                        <p className='text-run'>{t('gefen.virtual-high-school.section2.text')}</p>
                        <p className='emphasis'>
                            <Trans i18nKey='gefen.virtual-high-school.section2.emphasis'>
                                <span className='marked-text'></span>
                            </Trans>
                        </p>
                    </div>
                    <div className='image-wrapper'>
                        <div className='purple-box'></div>
                        <img src={media.book} alt='' />
                        <div className='sparkle'>{media.sparkle}</div>
                    </div>
                </section>

                <section className='section-4'>
                    <div className='image-wrapper'>
                        <div className='triangle'>{media.triangle}</div>
                        <img src={media.mat} alt='' />
                        <div className='purple-box'></div>
                    </div>
                    <p className='text-run'>{t('gefen.virtual-high-school.section3.text')}</p>
                </section>

                <section className='section-5'>
                    <p className='headline'>
                        <Trans i18nKey='gefen.virtual-high-school.section4.headline'>
                            <span className='marked-text'></span>
                        </Trans>
                    </p>
                    <p className='details'>{t('gefen.virtual-high-school.section4.details')}</p>
                </section>

                <section className='section-6'>
                    <p className='subtitle'>
                        <Trans i18nKey='gefen.virtual-high-school.section5.title'>
                            <span className='marked-text'></span>
                        </Trans>
                    </p>
                    <div className='steps'>
                        {[
                            { id: 1, lines: media.stepLines1, img: media.student },
                            { id: 2, lines: media.stepLines2, img: media.mathBook },
                            { id: 3, lines: media.stepLines3, img: media.computer },
                        ].map(({ id, lines, img }) => (
                            <div className={`step step-${id}`} key={id}>
                                <div className='visual'>
                                    <div className='square'>
                                        <div className='lines'>
                                            <StopMotionSvg shapes={lines.shapes} width={lines.width} height={lines.height}
                                                interval={interval} stroke='#F6BA55' strokWidth={9} strokeLinecap='round' />
                                        </div>
                                        <img src={img} alt='' />
                                    </div>
                                </div>
                                <p className='text-run'>{t(`gefen.virtual-high-school.section5.step${id}`)}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className='section-7'>
                    <div className='flex-column'>
                        <h3 className='heading'>
                            <Trans i18nKey='gefen.virtual-high-school.section6.title'>
                                <span className='marked-text'></span>
                            </Trans>
                        </h3>
                        <p className='text-run'>{t('gefen.virtual-high-school.section6.text')}</p>
                    </div>
                    <img className='staircase' src={media.staircase} alt='' />
                </section>

                <section className='section-8'>
                    <h3 className='heading'>
                        <Trans i18nKey='gefen.virtual-high-school.section7.title'>
                            <span className='marked-text'></span>
                        </Trans>
                    </h3>
                    <div className='row'>
                        <div className='flex-column'>
                            <p className='text-run text-1'>{t('gefen.virtual-high-school.section7.text1')}</p>
                            <p className='text-run text-2'>{t('gefen.virtual-high-school.section7.text2')}</p>
                            <p className='text-run text-3'>{t('gefen.virtual-high-school.section7.text3')}</p>
                            <div className='contact-lines'>
                                <a className='contact-line' href='mailto:virtualhighschool@lnet.co.il'>
                                    <span className='label'>{t('gefen.virtual-high-school.section7.emailLabel')}</span>
                                    <span className='value'>virtualhighschool@lnet.co.il</span>
                                </a>
                                <a className='contact-line' href='tel:0737052543'>
                                    <span className='label'>{t('gefen.virtual-high-school.section7.phoneLabel')}</span>
                                    <span className='value'>073-7052543</span>
                                </a>
                            </div>
                            <CommonButton text={t('gefen.blended-tools.section5.ctaButton')}
                            routePath='/contact-us' 
                                containerPage='gefen marathon rain'
                            />
                        </div>
                        <div className='image-wrapper'>
                            <div className='purple-box'></div>
                            <div className='triangle'>{media.triangle}</div>
                            <img className='jump' src={media.jumping} alt='' />
                        </div>
                    </div>
                    <h3 className='heading'>
                        <Trans i18nKey='gefen.virtual-high-school.section7.closing'>
                            <span className='marked-text'></span>
                        </Trans>
                    </h3>
                </section>
            </div>
        </div>
    );
};

export default VirtualHighSchool;